import { useRef, useCallback } from "react";
import {
  RocketLaunch, MagnifyingGlass, Lightning, User, Kanban,
  Bell, Globe, Cpu, BookOpen, ArrowRight, CheckCircle,
  Target, Buildings, UsersFour, FileText, Envelope, LinkedinLogo,
  ArrowSquareOut, ShieldCheck, Gear, Clock, Star,
  CaretRight, Sparkle, ChartBar, Warning, Info
} from "@phosphor-icons/react";

// ── Section anchor nav ──
const NAV_ITEMS = [
  { id: "quickstart", label: "Quick Start",   icon: RocketLaunch },
  { id: "personas",   label: "Personas",      icon: User         },
  { id: "search",     label: "Search",        icon: MagnifyingGlass },
  { id: "scoring",    label: "AI Scoring",    icon: Lightning    },
  { id: "learning",   label: "Self-Learning", icon: Sparkle      },
  { id: "apply",      label: "One-Click Apply", icon: CheckCircle },
  { id: "pipeline",   label: "Pipeline",      icon: Kanban       },
  { id: "radar",      label: "Job Radar",     icon: Bell         },
  { id: "watchlist",  label: "Watchlist",     icon: Target       },
  { id: "autoapply",  label: "Auto-Apply",    icon: ArrowSquareOut },
  { id: "settings",   label: "Settings",      icon: Gear         },
  { id: "generator",  label: "Generator",     icon: Globe        },
  { id: "research",   label: "Research",      icon: Buildings    },
  { id: "activitylog",label: "Activity Log",  icon: ChartBar     },
  { id: "tools",      label: "Tool Stack",    icon: Cpu          },
  { id: "tips",       label: "Tips",          icon: Star         },
];

// ── Persona color map ──
const PERSONA_COLORS = [
  { label: "Amber",   ring: "#F59E0B", bg: "rgba(245,158,11,0.12)",   text: "#F59E0B",   ex: "Senior Dev"       },
  { label: "Emerald", ring: "#10B981", bg: "rgba(16,185,129,0.12)",   text: "#10B981",   ex: "ML Engineer"      },
  { label: "Sky",     ring: "#38BDF8", bg: "rgba(56,189,248,0.12)",   text: "#38BDF8",   ex: "Frontend Eng."    },
  { label: "Purple",  ring: "#A855F7", bg: "rgba(168,85,247,0.12)",   text: "#A855F7",   ex: "Product Manager"  },
];

// ── Job sources ──
const SOURCES = [
  { name: "LinkedIn",    dot: "#0A66C2" },
  { name: "Indeed",      dot: "#2164F3" },
  { name: "Glassdoor",   dot: "#0CAA41" },
  { name: "Google Jobs", dot: "#EA4335" },
  { name: "ZipRecruiter",dot: "#4F9F38" },
  { name: "DuckDuckGo",  dot: "#DE5833" },
  { name: "Naukri",      dot: "#3B82F6" },
  { name: "Foundit.in",  dot: "#6366F1" },
  { name: "Shine.com",   dot: "#8B5CF6" },
];

// ── Score bands ──
const SCORE_BANDS = [
  { range: "80–100", label: "Strong Match",  color: "#10B981", bg: "rgba(16,185,129,0.08)",  tip: "Apply today. Your profile fits 80%+ of the role requirements."   },
  { range: "60–79",  label: "Good Match",    color: "#F59E0B", bg: "rgba(245,158,11,0.08)",  tip: "Worth applying. Tailor your cover letter for missing keywords."  },
  { range: "40–59",  label: "Stretch Role",  color: "#F97316", bg: "rgba(249,115,22,0.08)",  tip: "Apply selectively. You'd need to upskill in 2-3 key areas."      },
  { range: "0–39",   label: "Weak Match",    color: "#EF4444", bg: "rgba(239,68,68,0.08)",   tip: "Skip or use for market research only."                           },
];

// ── Tool replacements ──
const TOOL_REPLACEMENTS = [
  { old: "Brave Search API",  neo: "DuckDuckGo (ddgs)",    desc: "Web-wide job search & dorking",       free: true  },
  { old: "Tavily API",        neo: "DDGS + LLM summary",   desc: "Company research & news intel",       free: true  },
  { old: "Firecrawl",         neo: "Jina Reader (r.jina.ai)", desc: "Career page HTML → Markdown",      free: true  },
  { old: "Hunter.io",         neo: "DDGS LinkedIn dorking",desc: "Hiring manager contact finding",      free: true  },
  { old: "Apify",             neo: "Crawl4AI (HTTP)",      desc: "Deep async page scraping",            free: true  },
  { old: "Job board APIs",    neo: "python-jobspy",        desc: "Multi-source job aggregation",        free: true  },
];

// ── Reusable section header ──
function SectionHead({ icon: Icon, label, title, subtitle, color = "#F59E0B" }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
          <Icon size={15} weight="fill" style={{ color }} />
        </div>
        <span className="jp-label">{label}</span>
      </div>
      <h2 className="font-['Outfit'] text-2xl font-semibold text-white tracking-tight">{title}</h2>
      {subtitle && <p className="text-sm text-zinc-500 mt-1.5 leading-relaxed">{subtitle}</p>}
    </div>
  );
}

// ── Mini score ring ──
function ScoreRing({ score, size = 64, color }) {
  const r = size / 2 - 5;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={4} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={4}
        strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
        style={{ transition: "stroke-dashoffset 1s cubic-bezier(0.4,0,0.2,1) 0.2s" }} />
    </svg>
  );
}

// ── Pipeline step ──
function PipelineStep({ label, color, count = 0 }) {
  return (
    <div className="flex flex-col items-center gap-1.5 min-w-[70px]">
      <div className="w-14 h-14 rounded-lg flex items-center justify-center text-xs font-mono font-bold"
        style={{ background: `${color}12`, border: `1.5px solid ${color}35`, color }}>
        {count > 0 ? count : ""}
      </div>
      <span className="text-[10px] text-zinc-500 text-center leading-tight">{label}</span>
    </div>
  );
}

export default function GuidePage() {
  const sectionRefs = useRef({});

  const scrollTo = useCallback((id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const ref = (id) => (el) => { sectionRefs.current[id] = el; };

  return (
    <div className="relative" data-testid="guide-page">

      {/* ── Sticky section nav ── */}
      <div className="guide-section-nav bg-[#050505]/90 border-b border-white/5 px-6 py-2.5">
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide" data-testid="guide-nav">
          {NAV_ITEMS.map(item => (
            <button key={item.id} onClick={() => scrollTo(item.id)}
              data-testid={`guide-nav-${item.id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-zinc-500 hover:text-white hover:bg-white/5 transition-colors whitespace-nowrap shrink-0">
              <item.icon size={12} weight="fill" />
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 lg:px-10 pb-20 max-w-[920px]">

        {/* ══════════════════════════════ HERO ══════════════════════════════ */}
        <div className="pt-10 pb-8 animate-in" data-testid="guide-hero">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/20 bg-amber-500/5 mb-5">
            <BookOpen size={13} weight="fill" className="text-amber-500" />
            <span className="text-[11px] text-amber-400 font-medium tracking-wide">COMPLETE USER GUIDE</span>
          </div>
          <h1 className="font-['Outfit'] text-5xl font-bold tracking-tight mb-4">
            <span className="text-white">Master</span>{" "}
            <span className="jp-gradient-text">sankhya</span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-[580px] leading-relaxed">
            Everything you need to go from resume upload to job offer — using AI, zero paid APIs, and one cinematic interface.
          </p>

          {/* Key stats */}
          <div className="flex flex-wrap gap-4 mt-8 animate-in delay-2">
            {[
              { val: "9",     label: "Job Sources"         },
              { val: "100",   label: "AI Match Score"       },
              { val: "4",     label: "LLM Fallback Tiers"   },
              { val: "130+",  label: "Target Companies"     },
              { val: "5",     label: "Filter Dimensions"    },
              { val: "0",     label: "Paid APIs Required"   },
            ].map(s => (
              <div key={s.label} className="flex items-baseline gap-2 px-4 py-2.5 rounded-lg border border-white/8 bg-white/[0.02]">
                <span className="font-['Outfit'] text-2xl font-bold text-amber-500">{s.val}</span>
                <span className="text-xs text-zinc-600">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════ QUICK START ══════════════════════════════ */}
        <div ref={ref("quickstart")} className="scroll-mt-12 mb-12 animate-in delay-3" data-testid="guide-quickstart">
          <SectionHead icon={RocketLaunch} label="START HERE" title="4-Step Quick Start"
            subtitle="Get your first scored results in under 5 minutes." color="#F59E0B" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                step: "01", icon: User, color: "#F59E0B",
                title: "Create a Persona",
                desc: "On first launch, you'll see the 'Who's searching today?' screen. Create a persona with your name and target role. Think of it as a save slot — each one can have its own resume and search focus.",
                tip: "Name it clearly: 'Senior Dev' or 'ML Engineer' works better than 'Me'.",
              },
              {
                step: "02", icon: FileText, color: "#38BDF8",
                title: "Upload Your Resume",
                desc: "Go to Resume in the sidebar. Drag and drop any PDF. sankhya's AI extracts your skills, experience level, preferred roles, and years of experience in seconds.",
                tip: "Use a text-based PDF, not a scanned image. The cleaner your resume text, the better the matching.",
              },
              {
                step: "03", icon: MagnifyingGlass, color: "#10B981",
                title: "Run a Search",
                desc: "Go to Search. Describe your ideal role in plain English — no special syntax needed. sankhya expands your query and searches 9 sources simultaneously. A cinematic loading overlay shows progress.",
                tip: "Be specific: include seniority level, tech stack, and whether you prefer remote or on-site.",
              },
              {
                step: "04", icon: Lightning, color: "#A855F7",
                title: "AI Score & Apply",
                desc: "Jobs auto-score after every search. In Results, click 'Score All Unscored' to batch-score remaining jobs. A progress banner shows live status. Sort by score, click any job to see the match breakdown, then hit Apply Now.",
                tip: "Focus on 70+ scores first. Then use the Generator to write tailored cover letters.",
              },
            ].map((s, i) => (
              <div key={s.step}
                className={`jp-card p-5 flex gap-4 hover:border-white/20 transition-all animate-in delay-${i + 4}`}>
                <div className="shrink-0">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}>
                    <s.icon size={18} weight="fill" style={{ color: s.color }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs text-zinc-700">{s.step}</span>
                    <span className="text-sm font-semibold text-white font-['Outfit']">{s.title}</span>
                  </div>
                  <p className="text-xs text-zinc-500 leading-relaxed mb-2">{s.desc}</p>
                  <div className="flex items-start gap-1.5">
                    <Sparkle size={11} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
                    <span className="text-[10px] text-amber-500/80 italic">{s.tip}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════ PERSONAS ══════════════════════════════ */}
        <div ref={ref("personas")} className="scroll-mt-12 mb-12" data-testid="guide-personas">
          <div className="jp-card overflow-visible">
            <div className="jp-card-header">
              <SectionHead icon={User} label="PERSONA SYSTEM" title="Multi-Profile Search (Netflix-Style)"
                subtitle="Create up to 4 distinct search profiles — each with its own resume, scoring, and focus area. Switch between them like Netflix profiles." color="#A855F7" />
            </div>

            <div className="p-5">
              {/* Mini persona cards visual */}
              <div className="flex flex-wrap gap-3 mb-6">
                {PERSONA_COLORS.map((c, i) => (
                  <div key={c.label}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border animate-scale delay-${i + 1}`}
                    style={{ borderColor: `${c.ring}30`, background: c.bg, minWidth: 110 }}>
                    <div className="w-12 h-12 rounded-full flex items-center justify-center border-2"
                      style={{ borderColor: c.ring, background: `${c.ring}15` }}>
                      <span className="font-['Outfit'] text-xl font-bold" style={{ color: c.ring }}>
                        {c.ex.charAt(0)}
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-white font-['Outfit']">{c.ex}</span>
                    <span className="jp-badge" style={{ borderColor: `${c.ring}30`, color: c.ring, background: `${c.ring}10`, fontSize: 10 }}>
                      {c.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* How personas work */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { icon: User,        title: "Own Resume",    desc: "Each persona has a separate PDF upload. Scoring uses that persona's skills and experience — not a shared one." },
                  { icon: Target,      title: "Own Scoring",   desc: "When you search or score jobs, the AI compares against the active persona's profile. Switch persona → scores change." },
                  { icon: MagnifyingGlass, title: "Own Focus", desc: "Set a different role title per persona to guide Job Radar. 'ML Engineer' persona auto-searches ML jobs on schedule." },
                ].map((item, i) => (
                  <div key={item.title} className={`p-4 rounded-lg border border-white/6 bg-white/[0.02] animate-in delay-${i + 5}`}>
                    <item.icon size={16} weight="duotone" className="text-purple-400 mb-2" />
                    <div className="text-sm font-medium text-white mb-1">{item.title}</div>
                    <div className="text-xs text-zinc-500 leading-relaxed">{item.desc}</div>
                  </div>
                ))}
              </div>

              {/* Switch tip */}
              <div className="mt-4 p-3 rounded-lg border border-purple-500/15 bg-purple-500/5 flex items-start gap-2">
                <Info size={14} className="text-purple-400 mt-0.5 shrink-0" />
                <div className="text-xs text-purple-300/80 leading-relaxed">
                  <span className="font-medium">Switching:</span> Click the colored strip at the top of the sidebar — shows your persona initial, name, and role. The "⇄" icon opens the Netflix selector.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ SEARCH ══════════════════════════════ */}
        <div ref={ref("search")} className="scroll-mt-12 mb-12" data-testid="guide-search">
          <SectionHead icon={MagnifyingGlass} label="JOB SEARCH ENGINE" title="Natural Language, 9 Sources at Once"
            subtitle="Type your job description in plain English. sankhya's AI expands it into targeted queries and runs them across 9 sources simultaneously. A full-screen cinematic overlay shows search progress." color="#38BDF8" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Query example */}
            <div className="jp-card">
              <div className="jp-card-header"><span className="jp-label">EXAMPLE QUERY</span></div>
              <div className="p-4">
                <div className="jp-terminal text-sm p-4 rounded-lg" style={{ fontSize: 12 }}>
                  <span className="text-zinc-600">{">"} </span>
                  <span className="text-amber-400">"Senior Python backend engineer,</span>{"\n"}
                  <span className="text-amber-400">   ML-heavy, startup or scale-up,</span>{"\n"}
                  <span className="text-amber-400">   remote-first, not consulting"</span>
                </div>
                <div className="mt-3 text-xs text-zinc-500">
                  sankhya AI expands this into 2–4 targeted queries, then searches all 6 sources in parallel.
                </div>
              </div>
            </div>

            {/* Sources grid */}
            <div className="jp-card">
              <div className="jp-card-header"><span className="jp-label">SEARCH SOURCES</span></div>
              <div className="p-4 grid grid-cols-3 gap-2">
                {SOURCES.map((s, i) => (
                  <div key={s.name} className={`flex items-center gap-2 p-2 rounded-md bg-white/[0.02] animate-in delay-${i + 1}`}>
                    <span className="source-dot shrink-0" style={{ background: s.dot, width: 8, height: 8, borderRadius: '50%', display: 'inline-block' }} />
                    <span className="text-xs text-zinc-400">{s.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Search filters */}
          <div className="jp-card mt-4">
            <div className="jp-card-header"><span className="jp-label">SEARCH FILTERS</span></div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-3 text-zinc-600 font-medium">Filter</th>
                    <th className="text-left p-3 text-zinc-600 font-medium">Options</th>
                    <th className="text-left p-3 text-zinc-600 font-medium">Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { f: "Location",    opt: "India, Remote, Bangalore...",  rec: "Match your target market"           },
                    { f: "Work Mode",   opt: "Remote / Hybrid / On-site",    rec: "Remote opens global opportunities"  },
                    { f: "Job Type",    opt: "Full-time / Contract / Intern", rec: "Full-time for stable roles"        },
                    { f: "Recency",     opt: "24h / 3 days / 1 week",        rec: "3 days = quality + volume balance"  },
                    { f: "Results",     opt: "15 / 30 / 50 / 100",           rec: "Start with 30"                     },
                  ].map((row, i) => (
                    <tr key={row.f} className={`border-b border-white/4 ${i % 2 === 0 ? '' : 'bg-white/[0.01]'}`}>
                      <td className="p-3 text-zinc-300 font-medium">{row.f}</td>
                      <td className="p-3 text-zinc-500 font-mono">{row.opt}</td>
                      <td className="p-3 text-zinc-600">{row.rec}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ AI SCORING ══════════════════════════════ */}
        <div ref={ref("scoring")} className="scroll-mt-12 mb-12" data-testid="guide-scoring">
          <SectionHead icon={Lightning} label="AI MATCH SCORING" title="Know Exactly How Well You Fit"
            subtitle='Every job gets a 0-100 match score plus a detailed breakdown. "Score All Unscored" button triggers bulk background scoring with a live progress banner showing scored/total and percentage.' color="#F59E0B" />

          {/* Score bands */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            {SCORE_BANDS.map((band, i) => (
              <div key={band.range}
                className={`p-4 rounded-xl border flex items-start gap-4 animate-in delay-${i + 2}`}
                style={{ borderColor: `${band.color}25`, background: band.bg }}>
                <div className="relative shrink-0">
                  <ScoreRing score={parseInt(band.range.split("–")[0]) + 5} size={56} color={band.color} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-mono text-sm font-bold" style={{ color: band.color }}>
                      {band.range.split("–")[0]}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="font-medium text-white text-sm mb-1 font-['Outfit']">
                    {band.range} — <span style={{ color: band.color }}>{band.label}</span>
                  </div>
                  <div className="text-xs text-zinc-500 leading-relaxed">{band.tip}</div>
                </div>
              </div>
            ))}
          </div>

          {/* What scoring gives you */}
          <div className="jp-card">
            <div className="jp-card-header"><span className="jp-label">WHAT YOU GET WITH EVERY SCORE</span></div>
            <div className="grid grid-cols-2 md:grid-cols-4">
              {[
                { icon: Target,     label: "Match Score",      desc: "0–100 overall fit", color: "#F59E0B" },
                { icon: CheckCircle,label: "Match Reasons",    desc: "Why you're a fit",  color: "#10B981" },
                { icon: Warning,    label: "Missing Skills",   desc: "Gaps in your profile", color: "#F97316" },
                { icon: ChartBar,   label: "Gap Analysis",     desc: "Honest AI assessment", color: "#38BDF8" },
              ].map((item, i) => (
                <div key={item.label} className={`p-4 ${i < 3 ? 'border-r border-white/5' : ''} text-center`}>
                  <item.icon size={22} weight="duotone" style={{ color: item.color }} className="mx-auto mb-2" />
                  <div className="text-xs font-medium text-white mb-1">{item.label}</div>
                  <div className="text-[10px] text-zinc-600">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ SELF-LEARNING PARSER ══════════════════════════════ */}
        <div ref={ref("learning")} className="scroll-mt-12 mb-12" data-testid="guide-learning">
          <SectionHead icon={Sparkle} label="SELF-LEARNING ENGINE" title="Gets Smarter With Every Search"
            subtitle="sankhya analyzes every scored job description to learn your ideal role patterns. Over time, Radar and Watchlist use these insights for progressively smarter automation." color="#A855F7" />

          <div className="jp-card p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs font-medium text-purple-400 mb-3 font-mono">HOW IT LEARNS</div>
                <div className="space-y-3">
                  {[
                    { step: "1", title: "You search or Radar runs", desc: "Jobs are found from LinkedIn, Indeed, Google, career pages, etc." },
                    { step: "2", title: "Auto-scoring kicks in", desc: "Every job is scored against your resume. Match reasons, gaps, and keywords are extracted." },
                    { step: "3", title: "Parser analyzes patterns", desc: "Extracts top keywords, role titles, companies, and strong-match patterns from 60%+ scored jobs." },
                    { step: "4", title: "Smart queries generated", desc: "Builds optimized search queries (e.g. 'AI Product Manager', 'Growth PM') from the patterns." },
                    { step: "5", title: "Radar uses learned queries", desc: "Next Radar cycle uses smart queries instead of just your last search. Gets better over time." },
                  ].map(s => (
                    <div key={s.step} className="flex gap-3 items-start">
                      <div className="w-6 h-6 rounded-full bg-purple-500/15 border border-purple-500/30 flex items-center justify-center shrink-0">
                        <span className="text-[10px] font-bold text-purple-400 font-mono">{s.step}</span>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-white">{s.title}</div>
                        <div className="text-[11px] text-zinc-500 leading-relaxed">{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-purple-400 mb-3 font-mono">WHAT IT STORES (per persona)</div>
                <div className="space-y-2">
                  {[
                    { label: "Top Keywords", desc: "ai, data, stakeholder, platform, roadmap, strategy..." },
                    { label: "Learned Queries", desc: "AI Product Manager, Product Owner Agile, Growth PM, B2B SaaS PM..." },
                    { label: "Top Companies", desc: "Companies that frequently appear in your high-match results" },
                    { label: "Strong Match Patterns", desc: "Match reasons from 75%+ scored jobs — what makes you a great fit" },
                    { label: "Total Jobs Analyzed", desc: "Running count of all jobs the parser has learned from" },
                  ].map((item, i) => (
                    <div key={i} className="p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
                      <div className="text-[11px] font-medium text-amber-400 mb-0.5">{item.label}</div>
                      <div className="text-[10px] text-zinc-500">{item.desc}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-2.5 rounded-lg bg-purple-500/5 border border-purple-500/15">
                  <div className="text-[10px] text-purple-400 flex items-center gap-1.5">
                    <Info size={11} className="shrink-0" />
                    DB collection: <span className="font-mono text-white">persona_insights</span> — one document per persona, updated after every scoring cycle.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ RESULTS FILTERS ══════════════════════════════ */}
        <div className="scroll-mt-12 mb-12" data-testid="guide-filters">
          <SectionHead icon={MagnifyingGlass} label="RESULTS & FILTERS" title="Paginated Results with 5 Filter Dimensions"
            subtitle="Server-side pagination (50 per page) with full page controls. All filters run server-side for instant response even with thousands of jobs. Dashboard and Results always show consistent totals." color="#38BDF8" />

          <div className="jp-card p-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: "Text Search", desc: "Filter by job title, company name, or location (client-side)", color: "#F59E0B" },
                { label: "Score Range", desc: "Filter by 90%+, 75%+, or 60%+ (server-side query)", color: "#10B981" },
                { label: "Source", desc: "LinkedIn, Indeed, Glassdoor, Naukri, Watchlist, etc.", color: "#38BDF8" },
                { label: "Status", desc: "New, Saved, Applied — track your pipeline state", color: "#A855F7" },
                { label: "Sort", desc: "By match score, posting date, or company name (server-side)", color: "#71717A" },
              ].map(f => (
                <div key={f.label} className="p-3 rounded-lg border border-white/5" style={{ background: `${f.color}08` }}>
                  <div className="text-[11px] font-bold mb-1" style={{ color: f.color }}>{f.label}</div>
                  <div className="text-[10px] text-zinc-500 leading-relaxed">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ ONE-CLICK APPLY ══════════════════════════════ */}
        <div ref={ref("apply")} className="scroll-mt-12 mb-12" data-testid="guide-apply">
          <SectionHead icon={RocketLaunch} label="ONE-CLICK APPLY" title="Apply + Track in a Single Click"
            subtitle="Stop tracking applications in spreadsheets. One button opens the job URL and moves it to Applied in your pipeline — automatically." color="#10B981" />

          <div className="jp-card p-6">
            {/* Visual flow */}
            <div className="flex items-center justify-center gap-3 flex-wrap mb-6">
              {[
                { label: "You click 'Apply Now'", icon: RocketLaunch, color: "#F59E0B" },
                { label: "→", isArrow: true },
                { label: "Job URL opens in new tab", icon: ArrowSquareOut, color: "#38BDF8" },
                { label: "→", isArrow: true },
                { label: "Status → Applied", icon: CheckCircle, color: "#10B981" },
              ].map((step, i) => (
                step.isArrow
                  ? <ArrowRight key={i} size={18} className="text-zinc-700 jp-flow-arrow" />
                  : (
                    <div key={i} className={`flex flex-col items-center gap-2 p-4 rounded-xl border animate-in delay-${i + 1}`}
                      style={{ borderColor: `${step.color}25`, background: `${step.color}08`, minWidth: 140 }}>
                      <step.icon size={22} weight="duotone" style={{ color: step.color }} />
                      <span className="text-xs text-center text-zinc-400 leading-snug">{step.label}</span>
                    </div>
                  )
              ))}
            </div>

            {/* Two access points */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-amber-500/15 bg-amber-500/5">
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={16} weight="fill" className="text-amber-500" />
                  <span className="text-sm font-semibold text-white">From Job Drawer</span>
                </div>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Open any job → see the full amber <strong className="text-amber-400">"Apply Now — Track in Pipeline"</strong> button. One click does both. Shows "Applied! Job moved to Applied pipeline." confirmation.
                </p>
              </div>
              <div className="p-4 rounded-lg border border-emerald-500/15 bg-emerald-500/5">
                <div className="flex items-center gap-2 mb-2">
                  <RocketLaunch size={16} weight="fill" className="text-emerald-500" />
                  <span className="text-sm font-semibold text-white">From Results Board</span>
                </div>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  In the Results list, each job row has a <strong className="text-emerald-400">rocket icon</strong> in the action buttons. Hover to see it. Click → opens URL + marks Applied. No need to open the full drawer.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ PIPELINE ══════════════════════════════ */}
        <div ref={ref("pipeline")} className="scroll-mt-12 mb-12" data-testid="guide-pipeline">
          <SectionHead icon={Kanban} label="PIPELINE TRACKER" title="Kanban Board for Your Job Hunt"
            subtitle="Every application you save or apply to flows through a 5-stage visual pipeline. See your entire job search at a glance." color="#A855F7" />

          <div className="jp-card p-6">
            {/* Kanban visual */}
            <div className="flex items-start gap-2 overflow-x-auto pb-2 mb-5">
              {[
                { label: "Saved",     color: "#71717A", count: 0 },
                { isArrow: true },
                { label: "Applied",   color: "#38BDF8", count: 0 },
                { isArrow: true },
                { label: "Interview", color: "#A855F7", count: 0 },
                { isArrow: true },
                { label: "Offer",     color: "#10B981", count: 0 },
                { isArrow: true },
                { label: "Rejected",  color: "#EF4444", count: 0 },
              ].map((step, i) => (
                step.isArrow
                  ? <div key={i} className="self-center"><ArrowRight size={16} className="text-zinc-700 jp-flow-arrow" /></div>
                  : <PipelineStep key={i} label={step.label} color={step.color} />
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-zinc-500 leading-relaxed">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <Star size={14} weight="fill" className="text-zinc-400 mt-0.5 shrink-0" />
                <div><strong className="text-zinc-300">Saved:</strong> Jobs you want to track but haven't applied to yet. Use the ★ button in Results or open a job and change its status.</div>
              </div>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <RocketLaunch size={14} weight="fill" className="text-sky-400 mt-0.5 shrink-0" />
                <div><strong className="text-zinc-300">Applied:</strong> Auto-set when you click Apply Now. Or manually move via status dropdown in the job drawer.</div>
              </div>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <UsersFour size={14} weight="fill" className="text-purple-400 mt-0.5 shrink-0" />
                <div><strong className="text-zinc-300">Interview:</strong> Mark this when you receive a callback. Before the interview, use "Research Company" in the job drawer.</div>
              </div>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <CheckCircle size={14} weight="fill" className="text-emerald-400 mt-0.5 shrink-0" />
                <div><strong className="text-zinc-300">Offer / Rejected:</strong> Keep tracking until the very end. The dashboard shows your conversion funnel across all stages.</div>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ JOB RADAR ══════════════════════════════ */}
        <div ref={ref("radar")} className="scroll-mt-12 mb-12" data-testid="guide-radar">
          <SectionHead icon={Bell} label="JOB RADAR" title="Auto-Discovery While You Sleep"
            subtitle="Job Radar runs searches on a schedule, scores every new result against your resume, and sends you an alert only when a job scores above your threshold." color="#F59E0B" />

          <div className="jp-card">
            {/* Radar animation + flow */}
            <div className="p-6 flex flex-col md:flex-row items-center gap-8">
              {/* Animated radar */}
              <div className="relative w-28 h-28 shrink-0 flex items-center justify-center">
                {[0, 1, 2].map(i => (
                  <div key={i} className="absolute rounded-full border border-amber-500/20"
                    style={{
                      inset: `${i * 12}px`,
                      animation: `radarPing ${2 + i * 0.5}s ease-out ${i * 0.4}s infinite`
                    }} />
                ))}
                <div className="relative z-10 w-12 h-12 rounded-full bg-amber-500/15 border-2 border-amber-500/40 flex items-center justify-center jp-glow">
                  <Bell size={22} weight="fill" className="text-amber-500" />
                </div>
              </div>

              {/* How it works steps */}
              <div className="flex-1 grid grid-cols-1 gap-3">
                {[
                  { n: "01", title: "Schedule",  desc: "Runs automatically every 1–24h at your chosen interval.", color: "#F59E0B" },
                  { n: "02", title: "Discover",  desc: "Re-runs your saved search queries on Indeed & Google.",   color: "#38BDF8" },
                  { n: "03", title: "Score",     desc: "Every new job is AI-scored. Already-seen jobs are skipped.", color: "#10B981" },
                  { n: "04", title: "Alert",     desc: "Jobs above your score threshold appear as unread alerts.", color: "#A855F7" },
                ].map((s, i) => (
                  <div key={s.n} className={`flex items-center gap-3 animate-in-left delay-${i + 2}`}>
                    <span className="font-mono text-xs w-6 shrink-0" style={{ color: s.color }}>{s.n}</span>
                    <div className="w-px h-4 bg-white/10" />
                    <span className="text-xs font-medium text-white w-16 shrink-0">{s.title}</span>
                    <span className="text-xs text-zinc-500">{s.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Config reference */}
            <div className="border-t border-white/5 p-5">
              <div className="jp-label mb-3">CONFIGURATION REFERENCE</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { key: "Interval",  opts: "1h – 24h",   rec: "6h is the sweet spot"          },
                  { key: "Threshold", opts: "50–95%",      rec: "75–80% for quality alerts"     },
                  { key: "Freshness", opts: "12h – 3 days",rec: "24h for high-activity markets" },
                  { key: "Queries",   opts: "Up to 3",     rec: "Leave empty to auto-use last 3 searches" },
                ].map(c => (
                  <div key={c.key} className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                    <div className="text-xs font-medium text-amber-500 mb-1 font-mono">{c.key}</div>
                    <div className="text-[10px] text-zinc-400 mb-1">{c.opts}</div>
                    <div className="text-[10px] text-zinc-600 italic">{c.rec}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ TARGET COMPANIES WATCHLIST ══════════════════════════════ */}
        <div ref={ref("watchlist")} className="scroll-mt-12 mb-12" data-testid="guide-watchlist">
          <SectionHead icon={Target} label="TARGET COMPANIES" title="Track Dream Companies, Auto-Discover Jobs"
            subtitle="Add any company by name + career URL. sankhya's unified scraper (Jina Reader + Crawl4AI) monitors their career pages and surfaces new openings automatically." color="#A855F7" />

          <div className="jp-card p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* How it works */}
              <div>
                <div className="text-xs font-medium text-purple-400 mb-3 font-mono">HOW IT WORKS</div>
                <div className="space-y-3">
                  {[
                    { step: "1", title: "Add Companies", desc: "Enter company name + career page URL. If you skip the URL, sankhya auto-discovers it via DuckDuckGo." },
                    { step: "2", title: "Scan Career Pages", desc: "Click scan or let the cron do it. The unified scraper (Jina first, Crawl4AI fallback) fetches the page." },
                    { step: "3", title: "AI Extracts Jobs", desc: "LLM parses the scraped markdown and pulls out all job listings with titles, locations, and apply links." },
                    { step: "4", title: "Auto-Score & Alert", desc: "Each job is scored against your resume. High-match jobs appear as Auto-Apply candidates." },
                  ].map(s => (
                    <div key={s.step} className="flex gap-3 items-start">
                      <div className="w-6 h-6 rounded-full bg-purple-500/15 border border-purple-500/30 flex items-center justify-center shrink-0">
                        <span className="text-[10px] font-bold text-purple-400 font-mono">{s.step}</span>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-white">{s.title}</div>
                        <div className="text-[11px] text-zinc-500 leading-relaxed">{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Features */}
              <div>
                <div className="text-xs font-medium text-purple-400 mb-3 font-mono">FEATURES</div>
                <div className="space-y-2">
                  {[
                    "Unlimited companies — add as many as you want",
                    "Per-company scan, pause, resume, and remove",
                    "Expandable job list per company with match scores",
                    "Duplicate prevention — won't add same company twice",
                    "Career URL auto-discovery if not provided",
                    "Unified scraper: Jina Reader (fast) -> Crawl4AI (deep) -> httpx (fallback)",
                    "Persona-aware: jobs linked to your active persona for scoring",
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px] text-zinc-400">
                      <CheckCircle size={12} weight="fill" className="text-purple-500 shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ AUTO-APPLY ASSISTANT ══════════════════════════════ */}
        <div ref={ref("autoapply")} className="scroll-mt-12 mb-12" data-testid="guide-autoapply">
          <SectionHead icon={ArrowSquareOut} label="AUTO-APPLY ASSISTANT" title="Pre-Navigated Apply Links for Top Matches"
            subtitle="When a job scores above your threshold (default 85%), it surfaces as an Auto-Apply candidate with a direct apply link. You still click submit — sankhya just gets you there faster." color="#F59E0B" />

          <div className="jp-card p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/15">
                <div className="text-xs font-bold text-amber-400 mb-2">DISCOVER</div>
                <div className="text-[11px] text-zinc-400 leading-relaxed">Watchlist scans or Job Radar find new jobs matching your profile.</div>
              </div>
              <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/15">
                <div className="text-xs font-bold text-amber-400 mb-2">SCORE & FILTER</div>
                <div className="text-[11px] text-zinc-400 leading-relaxed">AI scores each job. Only those above your auto-apply threshold appear in the candidates list.</div>
              </div>
              <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/15">
                <div className="text-xs font-bold text-amber-400 mb-2">ONE-CLICK APPLY</div>
                <div className="text-[11px] text-zinc-400 leading-relaxed">Click the "Apply" button to open the job page pre-navigated. Fill in the form and submit.</div>
              </div>
            </div>
            <div className="mt-4 p-3 rounded-lg bg-white/[0.02] border border-white/5">
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Info size={14} className="text-amber-500 shrink-0" />
                Auto-Apply is "Option C" — it pre-navigates you to the apply page, but <strong className="text-white">you still submit manually</strong>. No automated form filling.
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ CONSOLIDATED SETTINGS ══════════════════════════════ */}
        <div ref={ref("settings")} className="scroll-mt-12 mb-12" data-testid="guide-settings">
          <SectionHead icon={Gear} label="SETTINGS PANEL" title="One Place for All Configuration"
            subtitle="Manage Job Radar schedules, Watchlist scanning intervals, and Auto-Apply thresholds from a single consolidated panel." color="#71717A" />

          <div className="jp-card p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { title: "Job Radar", desc: "Set scan interval (hours), minimum match score, target location, and monitored search queries.", color: "#F59E0B" },
                { title: "Watchlist Scanner", desc: "Configure how often career pages are re-scanned and the minimum score to surface jobs.", color: "#A855F7" },
                { title: "Auto-Apply", desc: "Set the match score threshold for auto-apply candidates. Toggle the feature on/off.", color: "#10B981" },
              ].map(s => (
                <div key={s.title} className="p-4 rounded-lg border border-white/5" style={{ background: `${s.color}08` }}>
                  <div className="text-xs font-bold mb-2" style={{ color: s.color }}>{s.title}</div>
                  <div className="text-[11px] text-zinc-400 leading-relaxed">{s.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ CONTENT GENERATOR ══════════════════════════════ */}
        <div ref={ref("generator")} className="scroll-mt-12 mb-12" data-testid="guide-generator">
          <SectionHead icon={Globe} label="AI CONTENT GENERATOR" title="3 Documents, Fully Personalised"
            subtitle="Every piece of content is tailored to the specific job and company — not generic templates." color="#38BDF8" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                icon: FileText, color: "#F59E0B",
                title: "Cover Letter",
                length: "Under 350 words",
                points: ["References company + role by name", "Highlights your most relevant skills", "Confident, non-generic tone", "Never starts with 'I am writing to...'"],
              },
              {
                icon: Envelope, color: "#38BDF8",
                title: "Cold Email",
                length: "Under 150 words",
                points: ["Compelling subject line included", "Value-focused, direct approach", "Clear call to action", "Designed for hiring managers"],
              },
              {
                icon: LinkedinLogo, color: "#10B981",
                title: "LinkedIn Message",
                length: "Under 300 characters",
                points: ["Fits LinkedIn's connection limit", "Specific to role + company", "Genuine, non-templated", "Stands out from bulk requests"],
              },
            ].map((doc, i) => (
              <div key={doc.title} className={`jp-card p-5 animate-in delay-${i + 2}`}
                style={{ borderTopColor: doc.color, borderTopWidth: 2 }}>
                <div className="flex items-center gap-2 mb-3">
                  <doc.icon size={18} weight="fill" style={{ color: doc.color }} />
                  <span className="font-semibold text-white text-sm font-['Outfit']">{doc.title}</span>
                </div>
                <div className="jp-badge mb-3" style={{ borderColor: `${doc.color}30`, color: doc.color, background: `${doc.color}10`, fontSize: 10 }}>
                  {doc.length}
                </div>
                <ul className="space-y-1.5">
                  {doc.points.map(pt => (
                    <li key={pt} className="flex items-start gap-2 text-xs text-zinc-500">
                      <CaretRight size={10} style={{ color: doc.color }} className="mt-0.5 shrink-0" />
                      {pt}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 text-[10px] text-zinc-700">
                  Access via Generator tab or open any job → Generate buttons in drawer
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════ RESEARCH TOOLS ══════════════════════════════ */}
        <div ref={ref("research")} className="scroll-mt-12 mb-12" data-testid="guide-research">
          <SectionHead icon={Buildings} label="RESEARCH TOOLS" title="Know Everything Before You Apply"
            subtitle="Three free research tools — no paid APIs. All powered by DuckDuckGo search + AI analysis. Contacts use AI fallback when LinkedIn dorking returns empty. All operations logged to activity_log." color="#10B981" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                icon: Buildings, color: "#10B981",
                title: "Company Research",
                how: "Click Research Company in any job drawer",
                replaces: "Tavily API",
                output: ["Company overview + industry", "Culture themes (from reviews)", "Recent news (last 6 months)", "Interview tips + talking points", "Red flags (if any)"],
              },
              {
                icon: UsersFour, color: "#F59E0B",
                title: "Hiring Manager Finder",
                how: "Click Find Contacts in job drawer",
                replaces: "Hunter.io",
                output: ["LinkedIn profile links", "Recruiter + manager names", "Role + department titles", "AI-generated contacts as fallback", "Connection message ready to send"],
              },
              {
                icon: Cpu, color: "#38BDF8",
                title: "Deep Scraper",
                how: "Search tab → Deep Scraper section",
                replaces: "Apify",
                output: ["Any URL — LinkedIn, Naukri, careers page", "Async HTTP extraction (no browser)", "AI extracts job listings from content", "Falls back to Jina Reader if needed"],
              },
            ].map((tool, i) => (
              <div key={tool.title} className={`jp-card p-5 animate-in delay-${i + 2}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <tool.icon size={16} weight="duotone" style={{ color: tool.color }} />
                    <span className="text-sm font-semibold text-white">{tool.title}</span>
                  </div>
                  <span className="text-[10px] text-zinc-700 line-through">{tool.replaces}</span>
                </div>
                <div className="text-[10px] text-zinc-600 italic mb-3 flex items-center gap-1">
                  <ArrowRight size={9} />
                  {tool.how}
                </div>
                <ul className="space-y-1">
                  {tool.output.map(o => (
                    <li key={o} className="flex items-start gap-1.5 text-xs text-zinc-500">
                      <CheckCircle size={10} style={{ color: tool.color }} className="mt-0.5 shrink-0" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════ ACTIVITY LOG ══════════════════════════════ */}
        <div ref={ref("activitylog")} className="scroll-mt-12 mb-12" data-testid="guide-activitylog">
          <SectionHead icon={ChartBar} label="ACTIVITY LOG" title="Full Transparency on Every Operation"
            subtitle="Every search, scoring run, research call, and error is logged to the activity_log Postgres table. Use it to debug scoring failures, track system behavior, and verify automation is working." color="#38BDF8" />

          <div className="jp-card p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs font-medium text-sky-400 mb-3 font-mono">LOGGED ACTIONS</div>
                <div className="space-y-2">
                  {[
                    { action: "search", desc: "Every job search with terms, sites used, jobs found" },
                    { action: "auto_score", desc: "Background scoring batches with success/failure counts" },
                    { action: "research_company", desc: "Company research calls with company name" },
                    { action: "find_contacts", desc: "Hiring manager searches with contacts found count" },
                    { action: "scoring errors", desc: "Failed scoring attempts with error messages" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
                      <span className="font-mono text-[10px] text-sky-400 w-24 shrink-0">{item.action}</span>
                      <span className="text-[11px] text-zinc-500">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-sky-400 mb-3 font-mono">EACH LOG ENTRY CONTAINS</div>
                <div className="space-y-2">
                  {[
                    { field: "action", desc: "What happened (search, auto_score, research_company...)" },
                    { field: "status", desc: "success or error" },
                    { field: "details", desc: "Context object (search terms, company name, counts...)" },
                    { field: "error", desc: "Error message if status is error (null otherwise)" },
                    { field: "created_at", desc: "ISO timestamp of when the event occurred" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
                      <span className="font-mono text-[10px] text-amber-400 w-20 shrink-0">{item.field}</span>
                      <span className="text-[11px] text-zinc-500">{item.desc}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-2.5 rounded-lg bg-sky-500/5 border border-sky-500/15">
                  <div className="text-[10px] text-sky-400 flex items-center gap-1.5 font-mono">
                    <Info size={11} className="shrink-0" />
                    API: GET /api/activity-log?limit=50&action=search&status=error
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ TOOL STACK ══════════════════════════════ */}
        <div ref={ref("tools")} className="scroll-mt-12 mb-12" data-testid="guide-tools">
          <SectionHead icon={Cpu} label="FREE TOOL STACK" title="Zero Paid APIs Required"
            subtitle="Every expensive SaaS tool has been replaced with a free, open-source alternative. No credit cards, no rate limits, no subscriptions." color="#A855F7" />

          <div className="jp-card overflow-hidden">
            {/* LLM chain */}
            <div className="p-5 border-b border-white/5">
              <div className="jp-label mb-3">LLM FALLBACK CHAIN</div>
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { name: "OpenAI GPT-4.1", tier: "PRIMARY",  color: "#10B981", detail: "Your key — configure in settings" },
                  { isArrow: true },
                  { name: "Claude Sonnet",  tier: "FALLBACK 1", color: "#38BDF8", detail: "Your key — configure in settings" },
                  { isArrow: true },
                  { name: "Groq / Llama 3", tier: "FALLBACK 2", color: "#F59E0B", detail: "Your key — free tier"         },
                  { isArrow: true },
                  { name: "Gemini 1.5 Flash", tier: "FALLBACK 3", color: "#A855F7", detail: "Your key — free forever"    },
                ].map((item, i) => (
                  item.isArrow
                    ? <ArrowRight key={i} size={14} className="text-zinc-700 jp-flow-arrow" />
                    : (
                      <div key={i} className="flex flex-col gap-1 p-2.5 rounded-lg border text-center"
                        style={{ borderColor: `${item.color}25`, background: `${item.color}08`, minWidth: 130 }}>
                        <span className="text-[9px] font-mono" style={{ color: item.color }}>{item.tier}</span>
                        <span className="text-xs font-semibold text-white font-['Outfit']">{item.name}</span>
                        <span className="text-[10px] text-zinc-600">{item.detail}</span>
                      </div>
                    )
                ))}
              </div>
            </div>

            {/* Tool replacements table */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-3 text-zinc-600 font-medium">Paid Tool Removed</th>
                    <th className="text-left p-3 text-zinc-600 font-medium">Free Replacement</th>
                    <th className="text-left p-3 text-zinc-600 font-medium">What It Does</th>
                    <th className="text-left p-3 text-zinc-600 font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {TOOL_REPLACEMENTS.map((row, i) => (
                    <tr key={row.old} className={`border-b border-white/4 ${i % 2 === 0 ? '' : 'bg-white/[0.01]'}`}>
                      <td className="p-3 text-red-400/70 line-through text-[11px]">{row.old}</td>
                      <td className="p-3 text-emerald-400 font-medium text-[11px]">{row.neo}</td>
                      <td className="p-3 text-zinc-500 text-[11px]">{row.desc}</td>
                      <td className="p-3">
                        <span className="jp-badge jp-badge-emerald text-[10px]">FREE</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════ TIPS ══════════════════════════════ */}
        <div ref={ref("tips")} className="scroll-mt-12 mb-12" data-testid="guide-tips">
          <SectionHead icon={Star} label="TIPS & TRICKS" title="Get Maximum Results"
            subtitle="Patterns from power users that consistently surface better jobs and higher response rates." color="#F59E0B" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { icon: MagnifyingGlass, color: "#F59E0B", title: "Query variation matters",       tip: "Try 'Senior Python developer ML' AND 'Backend engineer NLP' — different keywords surface different jobs from the same sources." },
              { icon: Globe,           color: "#38BDF8", title: "Remote = global opportunities",  tip: "Set Work Mode to Remote + Location to 'Remote' to unlock worldwide roles. Great for maximising your option count." },
              { icon: Lightning,       color: "#10B981", title: "Auto-scoring is ON",             tip: "Every search now auto-scores jobs in the background. Just search and come back — scores, match reasons, and auto-apply candidates appear automatically." },
              { icon: Clock,           color: "#A855F7", title: "Use the 3-day recency filter",   tip: "Last 24h = freshest but fewer. Last week = more results but older. 3 days is the optimal balance for quality job discovery." },
              { icon: Buildings,       color: "#F97316", title: "Watchlist runs itself",           tip: "Add your dream companies once with career URLs. The cron scanner checks them every 12 hours, extracts jobs, scores them, and alerts you. Zero manual effort." },
              { icon: Bell,            color: "#EF4444", title: "Set radar threshold at 75-80%",   tip: "Too low (50%) = noise. Too high (90%) = no alerts. 75-80% is the sweet spot. Radar re-runs your saved searches every 6 hours automatically." },
              { icon: User,            color: "#FBBF24", title: "Use separate personas",          tip: "Maintain a 'Frontend Dev' persona with a frontend-focused resume and a 'Backend Dev' persona with a different PDF. Scores will differ significantly." },
              { icon: Target,          color: "#10B981", title: "132 companies, zero effort",      tip: "Your watchlist has 132 companies loaded. Enable the watchlist scanner in Settings and it monitors all of them on autopilot." },
              { icon: Gear,            color: "#71717A", title: "Settings = automation control",   tip: "One panel controls everything: Radar interval, Watchlist scan frequency, Auto-Apply threshold. Tune once, forget forever." },
              { icon: ArrowSquareOut,   color: "#F59E0B", title: "Auto-Apply gets you there",      tip: "Jobs scoring 85%+ appear as Auto-Apply candidates with direct apply links. Click Apply — the page opens pre-navigated. You just submit." },
            ].map((tip, i) => (
              <div key={tip.title}
                className={`flex items-start gap-3 p-4 rounded-xl border border-white/6 bg-white/[0.02] hover:border-white/12 transition-colors animate-in delay-${(i % 4) + 1}`}>
                <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center mt-0.5"
                  style={{ background: `${tip.color}12`, border: `1px solid ${tip.color}25` }}>
                  <tip.icon size={15} weight="fill" style={{ color: tip.color }} />
                </div>
                <div>
                  <div className="text-sm font-medium text-white mb-1 font-['Outfit']">{tip.title}</div>
                  <div className="text-xs text-zinc-500 leading-relaxed">{tip.tip}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════ FOOTER ══════════════════════════════ */}
        <div className="jp-card p-6 text-center animate-in">
          <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center mx-auto mb-3 jp-glow">
            <Lightning size={20} weight="fill" className="text-black" />
          </div>
          <div className="font-['Outfit'] text-lg font-semibold text-white mb-1">sankhya v5</div>
          <div className="text-xs text-zinc-600 mb-1">FastAPI · React · Postgres · pgvector · OpenAI · Claude · Groq · Gemini · Crawl4AI · Jina Reader · DuckDuckGo · Activity Log</div>
          <div className="text-[10px] text-zinc-700">132 target companies · 16 features · 4 LLM fallback tiers · 9 job sources · 0 paid APIs</div>
          <div className="flex justify-center gap-3 mt-4 flex-wrap">
            {["python-jobspy", "crawl4ai", "duckduckgo-search", "openai", "apscheduler", "jina-reader", "activity-log"].map(lib => (
              <span key={lib} className="jp-badge jp-badge-zinc">{lib}</span>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
